import { type LocationDescriptor } from "history";
import { PureComponent } from "react";
import { type match, useHistory } from "react-router-dom";

import { cn } from "@btnet/utilities-core";

import {
    BdsButton,
    BdsIconPencilSimple,
    BdsIconPlus,
    BdsLink,
    BdsSelect,
    type BdsSelectProps,
} from "@buildertrend/components";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { track } from "utilities/analytics/analytics";
import { alphabetize, getByValue } from "utilities/form/form";

import { LinkButton } from "commonComponents/btWrappers/LinkButton/LinkButton";
import {
    CollectionEditable,
    type IDetailProps,
    type IDisplayProps,
} from "commonComponents/utilities/CollectionEditable/CollectionEditable";

import "./BTSelectEditable.less";

import { BTSelect, type BTSelectProps } from "../BTSelect/BTSelect";

interface IViewOptions {
    /** Set true if user has permission to view */
    canView: boolean;

    /** Placeholder text to be displayed inside of the BTSelect field if canView is false */
    notVisiblePlaceholder: string;
}

type DetailPathType = string | ((match: match<{ [key: string]: string | undefined }>) => string);

// defines the parameter required by generatePath in react router.
interface IGeneratePathObject {
    [paramName: string]: string | number | boolean | undefined;
}

type PathObjectGeneratorType<ExtraDataType> = (
    selectedItem: BTSelectItem<ExtraDataType>,
) => IGeneratePathObject;

type EditLinkGeneratorType<ExtraDataType> = (
    selectedItem: BTSelectItem<ExtraDataType>,
) => LocationDescriptor;

/**
 *  This route props interface is used to define the routes for adding, editing, and viewing items in the BTSelectEditable component.
 *  Instead of using the deprecated addLink, editLink, and viewLink, use the addRoute, editRoute, and viewRoute properties and pass the full route as a LocationDescriptor.
 */
interface IRouteProps<ExtraDataType> {
    /**
     * A path that will be navigated to, to load your details component for adding a new item.
     */
    addRoute?: LocationDescriptor;
    /**
     * A path that will be navigated to, to load your details component for editing an existing item.
     */
    editRoute?: EditLinkGeneratorType<ExtraDataType>;
    /**
     * A path that will be navigated to, to load your details component for viewing an existing item.
     */
    viewRoute?: LocationDescriptor;

    detailPath: DetailPathType;
    addLink?: never; // deprecated, use addRoute instead
    editLink?: never; // deprecated, use editRoute instead
    queryString?: never; // deprecated, use the query string in the individual route props instead
}

/**
 * @deprecated
 * This is the deprecated route props interface. This interface is the old way of defining the routes for adding, editing, and viewing items in the BTSelectEditable component.
 * It is recommended to use the IRouteProps interface instead, but this interface is kept for backward compatibility until it can be removed.
 */
interface IDeprecatedRouteProps<ExtraDataType> {
    /**
     * A path that will be navigated to, to load your details component.
     *
     * THIS PATH IS RELATIVE TO THE PARENT COMPONENT.
     * @example (The parent page url = '/parent/1/2')
     * detailPath="/child/:id"
     */

    detailPath: DetailPathType;

    /**
     * @deprecated use the individual route props instead (addRoute, editRoute, viewRoute)
     * Object that fills in the URL variables of detailPath to link to a new page. Usually sets some
     * "id" variable to 0 in order to link to a "new" item
     * @example
     * detailPath={"/child/:detailId"}
     * addLink={{detailId: 0}}
     */
    addLink: IGeneratePathObject;

    /**
     * @deprecated use the individual route props instead (addRoute, editRoute, viewRoute)
     * Function that returns an object that fills in the URL variables of detailPath to link to a new page.
     * Usually sets some "id" variable to the value of the BTSelectItem that was selected for editing/viewing
     * @example
     * detailPath={"/child/:detailId"}
     * editLink={(selectedItem: BTSelectItem)=>({detailId: selectedItem.value})}
     */
    editLink: PathObjectGeneratorType<ExtraDataType>;

    /**
     * @deprecated add the query string to the individual route props instead (addRoute, editRoute, viewRoute)
     *  Pass location.search from a base path to preserve QS values during routing within this component
     */
    queryString?: string;

    addRoute?: never;
    editRoute?: never;
    viewRoute?: never;
}

export type IBTEditableSelectProps<FormValues, ExtraDataType> = BTSelectProps<
    FormValues,
    ExtraDataType
> & {
    /**
     * Renders the details page for adding/editing a collection item
     * @example
     * renderDetail={props => {
     *     <BTModal>
     *         {{Insert add/edit/delete form here!!!}}
     *     </BTModal>
     * }}>
     */
    renderDetail: (props: IDetailProps<BTSelectItem<ExtraDataType>>) => JSX.Element;

    /**
     * Event handler for when the BTSelectItems have been modified, added to, or deleted from
     * @example
     * onTreeDataChange={(newTreeData, changedEntity) => {
     *    //
     *    // Check if selectedEntity(s) was deleted from newTreeData
     *    if( checkHere ) {
     *      this.setState({selectedEntity: undefined, treeData: newTreeData});
     *    } else {
     *      this.setState({treeData: newTreeData, });
     *    }
     *  }
     * }
     */
    onTreeDataChange: (
        newCollection: BTSelectItem<ExtraDataType>[],
        changedEntity: BTSelectItem<ExtraDataType>,
    ) => void;

    /** Set true if user has permission to add */
    canAdd: boolean;

    /** Set true if user has permission to edit */
    canEdit: boolean;

    /**
     * Configuration to enable view button.
     * If undefined, the view button will never be added.
     */
    viewOptions?: IViewOptions;

    /**
     * Set true when the field as a whole is configurable and Add/Edit don't make sense.  This will override `canAdd` & `canEdit`.
     * DO NOT USE unless discussed with UX
     * @default false
     */
    isConfigurable?: never;

    /**
     * A function that determines whether to disable the edit/view buttons given the currently selected single selectItem.
     * This will provide undefined when multiple items are selected.
     * @default () => undefined
     */
    isEditDisabled?: (item: BTSelectItem<ExtraDataType> | undefined) => boolean | undefined;

    /**
     * Set false when opting out of collection alphabetization in onTreeDataChange.
     * This is only used in onTreeDataChange
     * @default true
     */
    shouldAlphabetizeOnTreeDataChange?: boolean;

    /**
     * Set true to use BdsSelect and BdsButtons instead of BTSelect and LinkButton.
     * @default false
     */
    useBds?: boolean;

    /**
     * Controls the form interaction mode. When set to "click-to-edit", the component appears
     * like a similar "readonly" component, becoming editable on click; suitable for auto-save scenarios.
     * @default "always-editing"
     */
    formMode?: "always-editing" | "click-to-edit";
} & (IRouteProps<ExtraDataType> | IDeprecatedRouteProps<ExtraDataType>);

/**
 * This type will only ever be this type when isConfigurable is provided. The keys, addLink, editLink, canAdd, and canEdit should not be provided
 * since they don't make sense in this context. Also detailsPath should now always be a string since it should route to the details page for this configuration.
 */
interface IConfigurableProps<FormValues, ExtraDataType>
    extends Omit<
        IBTEditableSelectProps<FormValues, ExtraDataType>,
        "isConfigurable" | "detailPath" | "addLink" | "editLink" | "canAdd" | "canEdit"
    > {
    isConfigurable: boolean;
    detailPath: string;
    addLink?: never;
    editLink?: never;
    canAdd?: never;
    canEdit?: never;
    viewOptions?: never;
    isEditDisabled?: never;
}

const BdsLinkButton = (props: {
    buttonText: string;
    linkTo: string | LocationDescriptor;
    testid: string;
    testIdType: "add" | "settings" | "edit" | "view";
    disabled?: boolean;
}) => {
    const { buttonText, linkTo, testid, testIdType, disabled } = props;
    const history = useHistory();

    const onClick = () => {
        history.push(linkTo);
    };

    const datatestid = `${testid}${testIdType}`;

    if (testIdType === "settings" || testIdType === "view") {
        return (
            <BdsLink
                className="margin-left-zero bds-p-2 BTSelectEditable-addonLink"
                onClick={onClick}
                testid={datatestid}
            >
                {buttonText}
            </BdsLink>
        );
    }

    return (
        <BdsButton
            onClick={onClick}
            displayType="secondary"
            testid={datatestid}
            disabled={disabled}
            ariaLabel={testIdType === "add" ? "Add" : "Edit"}
            icon={testIdType === "add" ? <BdsIconPlus /> : <BdsIconPencilSimple />}
        />
    );
};

function isConfigurableProps<FormValues, ExtraDataType>(
    props:
        | IBTEditableSelectProps<FormValues, ExtraDataType>
        | IConfigurableProps<FormValues, ExtraDataType>,
): props is IConfigurableProps<FormValues, ExtraDataType> {
    return props.isConfigurable !== undefined;
}

class BTSelectEditableBase<FormValues, ExtraDataType = undefined> extends PureComponent<
    | IBTEditableSelectProps<FormValues, ExtraDataType>
    | IConfigurableProps<FormValues, ExtraDataType>,
    { isFocused: boolean }
> {
    static defaultProps = {
        isEditDisabled: () => undefined,
        shouldAlphabetizeOnTreeDataChange: true,
        formMode: "always-editing" as const,
    };

    constructor(
        props:
            | IBTEditableSelectProps<FormValues, ExtraDataType>
            | IConfigurableProps<FormValues, ExtraDataType>,
    ) {
        super(props);
        this.state = {
            isFocused: false,
        };
    }

    private onFocus = (event: React.FocusEvent<HTMLElement>) => {
        this.setState({ isFocused: true });
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    private onBlur = (field: string, isTouched: boolean) => {
        this.setState({ isFocused: false });
        this.props.onBlur(field, isTouched);
    };

    private renderButton = (
        linkTo: LocationDescriptor,
        testIdType: "add" | "settings",
        buttonText: string,
    ) => {
        const { disabled, "data-testid": testid, useBds } = this.props;
        if (disabled) {
            return <></>;
        }

        const datatestid = `${testid}${testIdType}`;

        if (useBds) {
            return (
                <BdsLinkButton
                    linkTo={linkTo}
                    testIdType={testIdType}
                    testid={testid}
                    buttonText={buttonText}
                />
            );
        }

        return (
            <LinkButton
                to={linkTo}
                className={`ActionButton ${testIdType}`}
                type="link"
                data-testid={datatestid}
                key={testIdType}
                dontUseMatchUrl
            >
                {buttonText}
            </LinkButton>
        );
    };

    private renderDisablableButton = (
        getLinkTo:
            | ((value: BTSelectItem<ExtraDataType>) => string | LocationDescriptor)
            | LocationDescriptor,
        singleSelectedItem: BTSelectItem<ExtraDataType> | undefined,
        testIdType: "edit" | "view",
        buttonText: string,
    ) => {
        const { disabled, "data-testid": testid, useBds } = this.props;
        if (disabled) {
            return <></>;
        }

        const datatestid = `${testid}${testIdType}`;

        const isDisabled = !singleSelectedItem || this.props.isEditDisabled!(singleSelectedItem);

        let linkTo: string | LocationDescriptor;
        if (isDisabled) {
            linkTo = "";
        } else if (typeof getLinkTo === "function") {
            linkTo = getLinkTo(singleSelectedItem);
        } else {
            linkTo = getLinkTo;
        }

        if (useBds) {
            return (
                <BdsLinkButton
                    linkTo={linkTo}
                    testIdType={testIdType}
                    testid={testid}
                    buttonText={buttonText}
                    disabled={isDisabled}
                />
            );
        }

        const button = (
            <LinkButton
                to={linkTo}
                className="ActionButton"
                type="link"
                data-testid={datatestid}
                key={testIdType}
                disabled={isDisabled}
                dontUseMatchUrl
            >
                {buttonText}
            </LinkButton>
        );

        return button;
    };

    private getAddonAfter(
        showAddButton: boolean,
        showEditButton: boolean,
        showViewButton: boolean,
        collectionProps: IDisplayProps,
        getEditLink?: PathObjectGeneratorType<ExtraDataType>,
        addLink?: IGeneratePathObject,
    ) {
        const { value, treeData, addonAfter, addRoute, editRoute, viewRoute } = this.props;

        if (showAddButton || showEditButton || showViewButton || addonAfter) {
            // If a single selectedItem is undefined or if the selectedItem has a falsy value or value < 0,
            // then we should disable the edit and view buttons
            let singleSelectedItem: BTSelectItem<ExtraDataType> | undefined = undefined;
            if (value !== undefined && Array.isArray(value) && value.length === 1 && value[0] > 0) {
                singleSelectedItem = getByValue(treeData, value[0]);
            } else if (typeof value === "number" ? value > 0 : value) {
                singleSelectedItem = getByValue(treeData, value);
            }

            const getDetailsLink = (item: BTSelectItem<ExtraDataType>) =>
                `${collectionProps.getDetailPath(getEditLink!(item))}${
                    this.props.queryString ?? ""
                }`;

            return (
                <div className="margin-left-zero padding-left-xs">
                    <div className="flex flex-row grow gap-1">
                        {showAddButton &&
                            this.renderButton(
                                addRoute ??
                                    `${collectionProps.getDetailPath(addLink!)}${
                                        this.props.queryString ?? ""
                                    }`,
                                "add",
                                "Add",
                            )}
                        {showEditButton &&
                            this.renderDisablableButton(
                                editRoute ?? getDetailsLink,
                                singleSelectedItem,
                                "edit",
                                "Edit",
                            )}
                        {showViewButton &&
                            this.renderDisablableButton(
                                viewRoute ?? getDetailsLink,
                                singleSelectedItem,
                                "view",
                                "View",
                            )}
                        {addonAfter}
                    </div>
                </div>
            );
        }

        return undefined;
    }
    private selectProps(
        collectionProps: IDisplayProps,
    ): BTSelectProps<FormValues, ExtraDataType> | BdsSelectProps<FormValues, ExtraDataType> {
        const {
            canEdit,
            canAdd,
            isConfigurable,
            viewOptions,
            addonAfter,
            detailPath,
            renderDetail,
            addLink,
            editLink,
            onTreeDataChange,
            isEditDisabled,
            shouldAlphabetizeOnTreeDataChange,
            queryString,
            formMode,
            ...selectProps
        } = this.props;

        let result: BTSelectProps<FormValues, ExtraDataType> = {
            ...selectProps,
            disabled:
                selectProps.disabled || (this.props.treeData && this.props.treeData.length === 0),
            value: selectProps.value,
            style: { ...this.props.style, minWidth: 0 },
            onFocus: this.onFocus,
            onBlur: this.onBlur,
        };

        // Handle placeholder for click-to-edit mode
        if (formMode === "click-to-edit") {
            const hasNoValue = this.props.multiple
                ? !this.props.value ||
                  (Array.isArray(this.props.value) && this.props.value.length === 0)
                : !this.props.value;

            if (hasNoValue && this.props.treeData && this.props.treeData.length > 0) {
                result = {
                    ...result,
                    value: undefined,
                    placeholder: this.props.placeholder || "--",
                };
            }
        }

        if (isConfigurableProps(this.props)) {
            return {
                ...result,
                addonAfter: !this.props.isConfigurable ? undefined : (
                    <>
                        {this.renderButton(
                            collectionProps.getDetailPath({}),
                            "settings",
                            "Settings",
                        )}
                        {addonAfter}
                    </>
                ),
            };
        } else {
            const renderAddButton =
                (!this.props.viewOptions || this.props.viewOptions.canView) && this.props.canAdd;
            const renderEditButton =
                (!this.props.viewOptions || this.props.viewOptions.canView) && this.props.canEdit;
            const renderViewButton =
                this.props.viewOptions && !this.props.canEdit
                    ? this.props.viewOptions.canView
                    : false;
            const viewModeEnabledAndCannotView =
                this.props.viewOptions && !this.props.viewOptions.canView;
            return {
                ...result,
                disabled: viewModeEnabledAndCannotView || result.disabled,
                value: viewModeEnabledAndCannotView ? undefined : result.value,
                notVisiblePlaceholder: viewModeEnabledAndCannotView
                    ? viewOptions!.notVisiblePlaceholder
                    : this.props.notVisiblePlaceholder,
                addonAfter: this.getAddonAfter(
                    renderAddButton ?? false,
                    renderEditButton ?? false,
                    renderViewButton,
                    collectionProps,
                    this.props.editLink,
                    this.props.addLink,
                ),
            };
        }
    }

    private onTreeDataChange = (
        newCollection: BTSelectItem<ExtraDataType>[],
        changedEntity: BTSelectItem<ExtraDataType>,
    ) => {
        this.props.onTreeDataChange(
            this.props.multiple || !this.props.shouldAlphabetizeOnTreeDataChange
                ? newCollection
                : alphabetize(newCollection),
            changedEntity,
        );
    };

    private itemWithoutTag = (item: BTSelectItem<ExtraDataType>) => {
        return item.title;
    };
    public render() {
        const { treeData, detailPath, renderDetail, useBds, multiple, formMode } = this.props;
        const { isFocused } = this.state;
        const isClickToEditViewMode =
            formMode === "click-to-edit" && !isFocused && !this.props.readOnly;

        return (
            <div
                className={cn("BTSelectEditable", {
                    ClickToEditViewMode: isClickToEditViewMode,
                })}
            >
                <CollectionEditable<BTSelectItem<ExtraDataType>, "id">
                    collection={treeData ? treeData : []}
                    idField="id"
                    onValuesChange={this.onTreeDataChange}
                    detailPath={detailPath}
                    renderDetail={renderDetail}
                >
                    {(props) => {
                        return (
                            <>
                                {useBds ? (
                                    <BdsSelect<FormValues, ExtraDataType>
                                        className="BTSelectEditable"
                                        customItemRender={this.itemWithoutTag}
                                        ariaLabel={this.props.ariaLabel}
                                        {...(this.selectProps(props) as BdsSelectProps<
                                            FormValues,
                                            ExtraDataType
                                        >)}
                                    />
                                ) : (
                                    <BTSelect<FormValues, ExtraDataType>
                                        className="BTSelectEditable"
                                        customItemRender={
                                            multiple ? undefined : this.itemWithoutTag
                                        }
                                        {...this.selectProps(props)}
                                    />
                                )}
                            </>
                        );
                    }}
                </CollectionEditable>
            </div>
        );
    }
}

export const BTSelectEditable = track((props) => ({
    component: `Select Editable - ${props["data-testid"]}`,
}))(BTSelectEditableBase);
